/**
 * @genesis_domain Settings — Preferences API Service
 * @genesis_version V9.1
 * @genesis_intent Centralized network transport for /api/preferences endpoints.
 *   Fetches and updates the authenticated user's preferences profile.
 * @genesis_boundaries FORBIDDEN from containing raw fetch() calls or UI logic.
 *   All transport routes through centralized apiClient. FORBIDDEN from using
 *   Logical OR (||) for fallbacks — Nullish Coalescing (??) only.
 * @genesis_ai_trail Phase 3 of Hybrid Sync Preferences. All network transport
 *   routes through the centralized apiRequest() function for standardized
 *   token injection and error handling.
 */

import { apiRequest } from '@/core/api/apiClient';
import type { UpdatePreferences, UserProfile } from '@shared/schemas.js';

/**
 * Fetch the authenticated user's preferences profile.
 * Backend returns { success: true, data: UserProfile }.
 */
export async function fetchPreferences(signal?: AbortSignal): Promise<UserProfile> {
    const response = await apiRequest<{ success: boolean; data: UserProfile }>(
        '/preferences',
        { signal },
    );
    return response.data;
}

/**
 * Update the authenticated user's preferences (upsert).
 * Backend validates against UpdatePreferencesSchema and returns { success: true, data: UserProfile }.
 */
export async function updatePreferences(
    data: UpdatePreferences,
): Promise<UserProfile> {
    const response = await apiRequest<{ success: boolean; data: UserProfile }>(
        '/preferences',
        {
            method: 'PATCH',
            body: JSON.stringify(data),
        },
    );
    return response.data;
}
